import DOMPurify from 'dompurify';
import { useEffect, useId, useLayoutEffect, useRef } from 'react';
import { isMobileOnly, useMobileOrientation } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useLocation } from 'react-router-dom';
import { handleHeaderColorScheme, handleScrollDestroy, scrollDestroySelector } from '../../entities/ComponentsSlice';
import { fetchProgress } from '../../entities/ProgressSlice';
import GalleryItem from '../../shared/gallery-item/GalleryItem';
import HelmetComponent from '../../shared/helmet-component/HelmetComponent';
import LoaderCircle from '../../shared/loader-circle/LoaderCircle';
import useModalService from '../../tools/hooks/useModalsService';
import useScroll from '../../tools/hooks/useScroll';
import { useUserService } from '../../tools/hooks/useUserService';
import Footer from '../../widgets/footer/Footer';
import FormModal from '../../widgets/form-modal/FormModal';
import Header from '../../widgets/header/Header';
import MenuMobile from '../../widgets/menu-mobile/MenuMobile';
import ModalMenu from '../../widgets/modal-menu/ModalMenu';
import PanoramaModal from '../../widgets/panorama-modal/PanoramaModal';
import style from './ProgressOnePage.module.scss';

const ProgressOnePage = () => {
  const months = [
    'января',
    'февраля',
    'марта',
    'апреля',
    'мая',
    'июня',
    'июля',
    'августа',
    'сентября',
    'октября',
    'ноября',
    'декабря',
  ];

  const { isLandscape } = useMobileOrientation();
  const dispatch = useDispatch();
  const progress = useSelector(state => state.progressInfo.progress);
  const fetchError = useSelector(state => state.progressInfo.fetchError);
  const containerRef = useRef(null);
  const { device } = useUserService();
  const scrollDestroy = useSelector(scrollDestroySelector);
  const id = useId();

  useEffect(() => {
    if (!progress) {
      dispatch(fetchProgress());
    }
    dispatch(handleScrollDestroy(true));
    return () => {
      dispatch(handleScrollDestroy(false));
    };
  }, [dispatch, progress]);

  const { slug } = useParams();
  const progressCurr = progress ? progress.filter(el => el.slug === slug)[0] : {};
  const day = progressCurr.title ? progressCurr.title.split('-')[2] : null;
  const year = progressCurr.title ? progressCurr.title.split('-')[0] : null;
  const month = months[new Date(progressCurr?.title)?.getMonth()];

  useEffect(() => {
    dispatch(handleHeaderColorScheme('blackOnTransparent'));
    return function () {
      dispatch(handleHeaderColorScheme(''));
    };
  }, []);

  const { scrollY } = useScroll();

  const { menuMobile, panoramaModel } = useModalService();

  const { pathname } = useLocation();

  useLayoutEffect(() => {
    if (scrollY < 50) {
      dispatch(handleHeaderColorScheme('blackOnTransparent'));
    } else {
      dispatch(handleHeaderColorScheme(''));
    }
  }, [scrollY, dispatch]);

  if (fetchError) return <div className={style.error}>Не удалось загрузить данные</div>;
  if (!progress) {
    return <LoaderCircle />;
  }

  return (
    <>
      <HelmetComponent
        page={pathname.slice(1)}
        title={`Динамика строительства (${day} ${month} ${year} ${pathname.slice(-1)} очередь) | ЖК Moments`}
        description={`Динамика строительства (${day} ${month} ${year} ${pathname.slice(
          -1,
        )} очередь). Официальный сайт ЖК бизнес-класса Moments от FORMA. Проекты, дома, квартиры, условия покупки и многое другое.`}
      />
      {!isMobileOnly && (
        <>
          <div ref={containerRef} className={`App ${!scrollDestroy ? '' : 'App--scroll'}`}>
            <Header />
            <>
              <div className={style.content}>
                <div className={style.intro}>
                  <div className={style.introInner}>
                    {device !== 'isMobile' && (
                      <button className={style.back} onClick={() => window.history.back()}>
                        Назад
                      </button>
                    )}

                    <div className={style.introBottom}>
                      <p className={style.title}>
                        <span>Динамика</span> <br />
                        <span className={style.titleString}>строительства</span>
                      </p>

                      <div className={style.introBottom__list}>
                        <h1 className={style.introBottom__date}>{`${day} ${month} ${year} (${pathname.slice(
                          -1,
                        )} очередь)`}</h1>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(progressCurr.text || ''),
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {device === 'isDesktop' && (
                    <span className={style.lineWrapper}>
                      <svg
                        className={style.line}
                        width='747'
                        height='735'
                        viewBox='0 0 747 735'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          className={style.path}
                          d='M0.620106 560L327.391 699.673C320.794 682.407 263.081 536.502 336.953 498.106C526.292 399.697 753.025 760.313 642.749 732.967C536.286 706.567 360.748 121.251 746.229 -211'
                          stroke='#E1E1E1'
                        />
                      </svg>
                    </span>
                  )}
                </div>

                <div className={style.gallery}>
                  <div className={style.gallery__main}>
                    <GalleryItem url={progressCurr.fileUrl[0]} mimeType={progressCurr.mimeType[0]} />
                  </div>

                  {progressCurr.fileUrl.length > 1 && (
                    <div className={style.gallery__secondary}>
                      {progressCurr.fileUrl.slice(1).map((i, index) => {
                        return (
                          <div key={id} className={style.gallery__item}>
                            <GalleryItem
                              url={progressCurr.fileUrl[index + 1]}
                              mimeType={progressCurr.mimeType[index + 1]}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </>

            <Footer />
          </div>
          {menuMobile && <MenuMobile />}
          {panoramaModel && <PanoramaModal />}
          <FormModal />
          <ModalMenu />
        </>
      )}
      {isMobileOnly && !isLandscape && (
        <>
          {menuMobile && <MenuMobile />}
          {panoramaModel && <PanoramaModal />}
          <FormModal />
          <div ref={containerRef} className={`App ${!scrollDestroy ? '' : 'App--scroll'}`}>
            <Header />
            <>
              <div className={style.content}>
                <div className={style.intro}>
                  <div className={style.introInner}>
                    {device !== 'isMobile' && (
                      <button className={style.back} onClick={() => window.history.back()}>
                        Назад
                      </button>
                    )}

                    <div className={style.introBottom}>
                      <p className={style.title}>
                        <p>Динамика</p>
                        <p className={style.titleString}>строительства</p>
                      </p>

                      <div className={style.introBottom__list}>
                        <span className={style.introBottom__date}>{`${day} ${month} ${year}`}</span>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(progressCurr.text || ''),
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {device === 'isDesktop' && (
                    <svg
                      className={style.line}
                      width='747'
                      height='735'
                      viewBox='0 0 747 735'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        className={style.path}
                        d='M0.620106 560L327.391 699.673C320.794 682.407 263.081 536.502 336.953 498.106C526.292 399.697 753.025 760.313 642.749 732.967C536.286 706.567 360.748 121.251 746.229 -211'
                        stroke='#E1E1E1'
                      />
                    </svg>
                  )}
                </div>

                <div className={style.gallery}>
                  <div className={style.gallery__main}>
                    <GalleryItem url={progressCurr.fileUrl[0]} mimeType={progressCurr.mimeType[0]} />
                  </div>

                  {progressCurr.fileUrl.length > 1 && (
                    <div className={style.gallery__secondary}>
                      {progressCurr.fileUrl.slice(1).map((i, index) => {
                        return (
                          <div key={id} className={style.gallery__item}>
                            <GalleryItem
                              url={progressCurr.fileUrl[index + 1]}
                              mimeType={progressCurr.mimeType[index + 1]}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </>

            <Footer />
          </div>
        </>
      )}
      {isMobileOnly && isLandscape && (
        <div className={style.layoutPlug}>Для корректной работы сайта, пожалуйста, переверните устройство</div>
      )}
    </>
  );
};

export default ProgressOnePage;
